import React, { useState, useCallback, useEffect } from "react";
import { debounce } from "lodash";
import { useAuth } from "../../../../../AuthContext";
import {
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import StopIcon from "@mui/icons-material/Stop";
import SearchIcon from "@mui/icons-material/Search";
import "../../../../static/styles/LeftContainerFilters.css";
import "../../../../static/styles/filters.css";

const LeftContainerFilters = ({
  onApplyFilters,
  filterConfig,
  onDrawingToggle = () => {},
  isDrawing = false,
  setCustomAOI = () => {},
  setFilterCleared,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [filters, setFilters] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const { axiosInstance } = useAuth();
  
  useEffect(() => {
    setExpanded(!!sessionStorage.getItem('customAOI') || 
    !!sessionStorage.getItem('currentFilters'));
  }, []);

  const clearFilters = () => {
    sessionStorage.removeItem('customAOI');
    setCustomAOI(false);
    setFilters({});
    setSuggestions({});
    onApplyFilters("", true);
    sessionStorage.removeItem('currentFilters');
  };

  const handleFilterChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setFilters({ ...filters, [field]: value });
  };

  const fetchResults = async (query, view, field) => {
    try {
      const response = await axiosInstance.get(`${view}?${field}=${query}`);
      return response.data.results || response.data;
    } catch (error) {
      console.error("Error fetching results:", error);
      return [];
    }
  };

  const handleTextChange = (view, field) => async (event, value) => {
    setFilters({ ...filters, [field]: value });

    if (value?.length >= 3) {
      debounceFetch(value, view, field);
    } else {
      setSuggestions({ [view]: [] });
    }
  };

  const debounceFetch = useCallback(
    debounce(async (query, view, field) => {
      const results = await fetchResults(query, field, view);
      setSuggestions((prev) => ({
        ...prev,
        [field]: results.length ? results : [{ name: "No results" }],
      }));
    }, 300),
    []
  );

  const toggleFilters = () => {
    setExpanded(!expanded);
  };

  const applyFilters = () => {
    let filter = "";
    filterConfig.forEach(({ field, view }) => {
      if (filters[field] !== undefined) {
        filter += `${view}=${filters[field] ? filters[field] : ""}&`;
      }
    });

    filter = filter.endsWith("&") ? filter.slice(0, -1) : filter;
    onApplyFilters(filter);
  };

  const toggleDrawing = () => {
    onDrawingToggle(!isDrawing);
    setFilterCleared(false);
  };

  return (
    <Box className="left-filters">
      <Box className={`extended-filters ${expanded ? "expanded" : ""}`}>
        {filterConfig
          .filter((config) => config.type && config.type !== "button")
          .map(({ field, type, placeholder, icon, label, title, view }) => (
            <Box className="filter" key={field}>
              {icon && <SearchIcon fontSize="small" />}
              {type === "text" ? (
                <Autocomplete
                  value={filters[field] || ""}
                  freeSolo
                  fullWidth
                  options={
                    suggestions[field]?.map((s) =>
                      s.name ? s.name : s.identifier
                    ) || []
                  }
                  getOptionLabel={(option) => (option.length ? option : "")}
                  onInputChange={handleTextChange(view, field)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={label || ""}
                      title={title || ""}
                      variant="outlined"
                      value={filters[field] || ""}
                      onChange={handleFilterChange(field)}
                      fullWidth
                    />
                  )}
                />
              ) : type === "checkbox" ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!filters[field]}
                      onChange={handleFilterChange(field)}
                    />
                  }
                  label={label}
                />
              ) : type !== "button" ? (
                <TextField
                  type={type}
                  label={label}
                  title={title}
                  placeholder={placeholder}
                  value={filters[field] || ""}
                  onChange={handleFilterChange(field)}
                  variant="outlined"
                  fullWidth
                />
              ) : null}
            </Box>
          ))}
        {filterConfig.some(({ action }) => action === "toggleDrawing") && (
          <Button
            onClick={toggleDrawing}
            startIcon={isDrawing ? <StopIcon /> : <EditIcon />}
            className={`filter-button ${isDrawing ? "activated" : ""}`}
            sx={{
              padding: "15px",
              gap: "10px",
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography
              className="filter-button-text"
              sx={{
                marginTop: "5px",
                fontWeight: "550",
                fontSize: "14px",
              }}
            >
              Draw AOI
            </Typography>
          </Button>
        )}
      </Box>

      <IconButton onClick={toggleFilters} className="icon">
        {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </IconButton>

      {expanded && (
        <Box className="advanced-filter-group">
          <Button
            onClick={clearFilters}
            sx={{
              minWidth: "50%",
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Typography
              className="button-clear"
              sx={{
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              Clear All
            </Typography>
          </Button>
          <Button
            onClick={applyFilters}
            sx={{
              minWidth: "50%",
            }}
          >
            <Typography
              className="button-main-left-filters"
              sx={{
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              Search
            </Typography>
          </Button>
        </Box>
      )}

      {!expanded && (
        <Button onClick={applyFilters}>
          <Typography
            className="button-main"
            sx={{
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            Search
          </Typography>
        </Button>
      )}
    </Box>
  );
};

export default LeftContainerFilters;
